import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getLoggedInUserProfile } from '../modules/profile/api';
import notificationStream from '../modules/notifications/stream';
import pushService from '../services/push';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // A stored user without a token is not a session — it is debris from a
    // half-cleared logout. Treating it as signed in makes the login page fire
    // authenticated bootstrap calls that can only 401.
    const stored = localStorage.getItem('user');
    if (!stored || !localStorage.getItem('token')) return null;
    try {
      return JSON.parse(stored);
    } catch (e) {
      localStorage.removeItem('user');
      return null;
    }
  });

  const login = (userData) => {
    setUser(userData); // updates context
    localStorage.setItem('user', JSON.stringify(userData));
    if (userData.token) localStorage.setItem('token', userData.token);
    // Open the live channel with this session's JWT. AuthContext owns the
    // token's lifecycle, so it owns connect/disconnect; everything else only
    // subscribes.
    try {
      const token = userData.token || localStorage.getItem('token');
      if (token) notificationStream.connect(token);
    } catch (e) { console.warn('Failed to open the notification stream', e); }

    // Register this browser for push. Provider-agnostic (see services/push):
    // which transport carries it is decided by configuration, not here. Fully
    // best-effort — a browser that blocks notifications still signs in normally.
    pushService.register().catch(() => {});
  };

  const logout = () => {
    // Stop push BEFORE the token is cleared — unregistering needs an authenticated
    // request, and a token left registered would keep delivering this user's
    // notifications to a shared machine after they signed out.
    try { pushService.unregister(); } catch (e) { /* best effort */ }

    setUser(null);
    localStorage.clear();
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    try { notificationStream.disconnect(); } catch (e) { /* already closed */ }
  };

  const refreshProfile = useCallback(async () => {
    try {
      const updatedUser = await getLoggedInUserProfile();
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (err) {
      console.error('Failed to refresh user profile', err);
      logout(); // Optional: logout on error
      return null;
    }
  }, []);

  // Re-fetch permissions without logging out on a transient failure. Used for the
  // real-time "permissions_changed" push, where a blip must not eject the user.
  const reloadPermissions = useCallback(async () => {
    try {
      const updatedUser = await getLoggedInUserProfile();
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (err) {
      console.warn('reloadPermissions failed (keeping session):', err);
      return null;
    }
  }, []);

  // Debounce so a burst of pushes (e.g. several groups edited at once) triggers
  // a single reload rather than a storm of profile fetches.
  const reloadTimer = useRef(null);
  const scheduleReload = useCallback(() => {
    if (reloadTimer.current) clearTimeout(reloadTimer.current);
    reloadTimer.current = setTimeout(() => { reloadPermissions(); }, 300);
  }, [reloadPermissions]);

  // A session restored from localStorage never runs login(), so the stream and
  // the push token are (re)established here too. FCM rotates tokens, so this
  // also keeps the registered one current.
  useEffect(() => {
    if (!user) return undefined;
    try {
      const token = localStorage.getItem('token');
      if (token) notificationStream.connect(token);
    } catch (e) { /* storage unavailable; the stream stays closed */ }
    pushService.register().catch(() => {});
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id || user?._id]);

  // The backend's real-time permission-change signal. It rides the same live
  // channel as notifications on purpose: a control signal must not depend on
  // the user having granted browser notification permission, which is what
  // delivering it over push would have required.
  useEffect(() => {
    const unsub = notificationStream.subscribe(({ event }) => {
      if (event === 'permissions.changed') scheduleReload();
    });
    return () => { try { unsub && unsub(); } catch (e) { /* already gone */ } };
  }, [scheduleReload]);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshProfile, reloadPermissions, role: user?.role }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
